import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import ExpansionSection from 'components/templates/ExpansionSection';
import RoutePrompt from 'components/shared/RoutePrompt';
import {
  Input,
  DatePicker,
  Autocomplete,
  Select,
  Checkbox,
  PhoneInput,
} from 'components/FormInputs';
import countries from 'constants/countries';
import { validateNewUserFormPatch, cleanUpData } from 'logic/userForm';
import { useFormik } from 'components/shared/Formik';

const ProfileForm = ({ panels, canChangeName, initialValues, onSubmit }) => {
  const [basicExpanded, setBasicExpanded] = React.useState(true);
  const [passwordExpanded, setPasswordExpanded] = React.useState(false);
  const intl = useIntl();

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      username: '',
      username_confirm: '',
      birthday: '',
      nationality: '',
      country_of_residence: '',
      language: '',
      phone_1: '',
      phone_2: '',
      show_phone_number: '',
      hide_last_name: '',
      isCompany: '',
      ...initialValues,
    },
    validateOnChange: false,
    enableReinitialize: true,
    validate: validateNewUserFormPatch({ intl, ...initialValues }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onSubmit(cleanUpData(values));
      } catch (err) {
        setSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    setBasicExpanded(panels.basic);
    setPasswordExpanded(panels.password);
  }, [panels]);

  const {
    errors,
    values,
    handleSubmit,
    handleChange,
    isSubmitting,
    pristine,
    setFieldValue,
    dirty,
    isValid,
    handleBlur,
  } = formik;

  return (
    <Box>
      <form onSubmit={handleSubmit} noValidate>
        <RoutePrompt when={(!pristine && dirty) || isSubmitting} />
        <ExpansionSection
          expanded={basicExpanded}
          summary={
            <FormattedMessage id="userform.basicInformation" defaultMessage="Basic information" />
          }
          onChange={() => setBasicExpanded(!basicExpanded)}
        >
          <Box flex={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Input
                  name="first_name"
                  value={values.first_name}
                  onChange={(e) => {
                    setFieldValue('first_name', e.target.value);
                    if (values.business_type === 'SOLETRADER' && values.last_name) {
                      setFieldValue(
                        'business_name',
                        `${e.target.value.trim()} ${values.last_name.trim()}`,
                      );
                    }
                  }}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="userform.firstName" defaultMessage="First name" />}
                  required
                  disabled={!canChangeName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  name="last_name"
                  value={values.last_name}
                  onChange={(e) => {
                    setFieldValue('last_name', e.target.value);
                    if (values.business_type === 'SOLETRADER' && values.first_name) {
                      setFieldValue(
                        'business_name',
                        `${values.first_name.trim()} ${e.target.value.trim()}`,
                      );
                    }
                  }}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="userform.lastName" defaultMessage="Last name" />}
                  required
                  disabled={!canChangeName}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Input
                  name="username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="userform.email" defaultMessage="Email address" />}
                  required
                  type="email"
                />
              </Grid>
              {values.username !== initialValues.username && (
                <Grid item xs={12} sm={6}>
                  <Input
                    name="username_confirm"
                    value={values.username_confirm}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    label={
                      <FormattedMessage
                        id="userform.confirmEmail"
                        defaultMessage="Confirm email address"
                      />
                    }
                    required
                    type="email"
                  />
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  name="birthday"
                  value={values.birthday || null}
                  onBlur={handleBlur}
                  onChange={(date) => {
                    if (date) {
                      setFieldValue('birthday', date);
                    }

                    if (date === null) {
                      setFieldValue('birthday', null);
                    }
                  }}
                  label={<FormattedMessage id="userform.birthday" defaultMessage="Birthday" />}
                  cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
                  clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
                  errors={errors}
                  minDate="1917-01-01"
                  maxDate="2017-01-01"
                  openTo="year"
                  required
                  disabled={!canChangeName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  onBlur={handleBlur}
                  name="nationality"
                  label={
                    <FormattedMessage id="userform.nationality" defaultMessage="Nationality" />
                  }
                  errors={errors}
                  value={values.nationality}
                  settings={{
                    disableClearable: true,
                    valueProp: 'code',
                    labelProp: 'name',
                  }}
                  onChange={(selected) => {
                    setFieldValue('nationality', selected.code);
                  }}
                  options={countries[intl.locale]}
                  required
                  disabled={!canChangeName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  onBlur={handleBlur}
                  name="country_of_residence"
                  label={
                    <FormattedMessage id="userform.country" defaultMessage="Country of residence" />
                  }
                  errors={errors}
                  value={values.country_of_residence}
                  settings={{
                    disableClearable: true,
                    valueProp: 'code',
                    labelProp: 'name',
                  }}
                  onChange={(selected) => {
                    setFieldValue('country_of_residence', selected.code);
                  }}
                  options={countries[intl.locale]}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Select
                  name="language"
                  value={values.language}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="userform.language" defaultMessage="Language" />}
                  required
                  options={[
                    { label: 'English', value: 'EN' },
                    { label: 'Svenska', value: 'SV' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneInput
                  name="phone_1"
                  value={values.phone_1}
                  onChange={(v) => setFieldValue('phone_1', v)}
                  placeholder={intl.formatMessage({
                    id: 'phone1',
                    defaultMessage: 'Phone (primary)',
                  })}
                  errors={errors}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneInput
                  name="phone_2"
                  value={values.phone_2}
                  onChange={(v) => setFieldValue('phone_2', v)}
                  placeholder={intl.formatMessage({
                    id: 'phone2',
                    defaultMessage: 'Phone (secondary)',
                  })}
                  errors={errors}
                />
              </Grid>
              {initialValues.has_fixed_cottages && (
                <Grid item xs={12}>
                  <Checkbox
                    name="show_phone_number"
                    onChange={handleChange}
                    value={values.show_phone_number}
                    label={
                      <FormattedMessage
                        id="userform.showPhone"
                        defaultMessage="Show my phone number in my ads and conversations"
                      />
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Checkbox
                  name="hide_last_name"
                  onChange={handleChange}
                  value={values.hide_last_name}
                  label={
                    <FormattedMessage
                      id="userform.showLastName"
                      defaultMessage="Show only the first letter of my last name except for bookings"
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Checkbox
                  name="isCompany"
                  onChange={handleChange}
                  value={values.isCompany}
                  label={
                    <FormattedMessage
                      id="userform.companyAccount"
                      defaultMessage="This is a company account"
                    />
                  }
                  disabled={!canChangeName}
                />
              </Grid>
            </Grid>
            {values.isCompany && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Select
                    name="business_type"
                    value={values.business_type}
                    onChange={(e) => {
                      setFieldValue('business_type', e.target.value);
                      if (
                        e.target.value === 'SOLETRADER' &&
                        values.first_name &&
                        values.last_name
                      ) {
                        setFieldValue(
                          'business_name',
                          `${values.first_name.trim()} ${values.last_name.trim()}`,
                        );
                      }
                    }}
                    onBlur={handleBlur}
                    errors={errors}
                    label={
                      <FormattedMessage id="userform.companyType" defaultMessage="Company type" />
                    }
                    options={[
                      {
                        value: 'BUSINESS',
                        label: intl.formatMessage({ id: 'Business', defaultMessage: 'Business' }),
                      },
                      {
                        value: 'ORGANIZATION',
                        label: intl.formatMessage({
                          id: 'Organization',
                          defaultMessage: 'Organization',
                        }),
                      },
                      {
                        value: 'SOLETRADER',
                        label: intl.formatMessage({
                          id: 'Sole Trader',
                          defaultMessage: 'Sole Trader',
                        }),
                      },
                    ]}
                    disabled={!canChangeName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    name="business_name"
                    value={
                      values.business_type === 'SOLETRADER'
                        ? (!!values.business_name && values.business_name) ||
                          (values.first_name && values.last_name
                            ? `${values.first_name} ${values.last_name}`
                            : '')
                        : values.business_name
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    label={
                      <FormattedMessage id="userform.companyName" defaultMessage="Company name" />
                    }
                    disabled={!canChangeName || values.business_type === 'SOLETRADER'}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </ExpansionSection>
        {initialValues.type === 'SOFT' && (
          <ExpansionSection
            expanded={passwordExpanded}
            summary={<FormattedMessage id="userform.password" defaultMessage="Password" />}
            onChange={() => setPasswordExpanded(!passwordExpanded)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Input
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  label={
                    <FormattedMessage id="userform.newPassword" defaultMessage="New password" />
                  }
                  type="password"
                  required={initialValues.type === 'SOFT'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input
                  name="confirm_password"
                  value={values.confirm_password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  label={
                    <FormattedMessage
                      id="userform.confirmNewPassword"
                      defaultMessage="Confirm new password"
                    />
                  }
                  type="password"
                  required={initialValues.type === 'SOFT'}
                />
              </Grid>
            </Grid>
          </ExpansionSection>
        )}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          disabled={pristine || !dirty || isSubmitting || !isValid}
        >
          {isSubmitting && <FormattedMessage id="Saving" defaultMessage="Saving..." />}
          {!isSubmitting && <FormattedMessage id="Save" defaultMessage="Save" />}
        </Button>
        <Box mt={2}>
          <Typography variant="body2" align="center">
            <FormattedMessage
              id="userform.acceptTermsClickButton"
              defaultMessage="By clicking on Save, you agree to our {link}"
              values={{
                link: (
                  <a
                    href={intl.formatMessage({ id: 'link.terms', defaultMessage: '/en/terms' })}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage id="Terms of Service" defaultMessage="Terms of Service" />
                  </a>
                ),
              }}
            />
          </Typography>
        </Box>
      </form>
    </Box>
  );
};

ProfileForm.propTypes = {
  panels: PropTypes.shape({
    basic: PropTypes.bool,
    password: PropTypes.bool,
  }).isRequired,
  canChangeName: PropTypes.bool,
  initialValues: PropTypes.shape({
    type: PropTypes.string,
    username: PropTypes.string,
    has_fixed_cottages: PropTypes.bool,
  }),
  onSubmit: PropTypes.func.isRequired,
};

export default ProfileForm;
